/**
 * The six components of the P1Plug design system, as React.
 *
 * Markup and class names are identical to design-system/bundle.js and are
 * styled by its unmodified stylesheet; test/ds-parity.test.tsx renders both
 * and compares. Props follow design-system/index.d.ts. React-only additions
 * (event handlers, links, rich cells) are marked "addition".
 */
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Tone } from '@p1plug/platform';

// Filled status marks, one silhouette per tone (copied from the bundle).
const MARKS: Record<Tone, string> = {
  success: '<circle cx="12" cy="12" r="10" fill="currentColor"/><path class="cut" d="M7.5 12.5l3 3 6-6.5"/>',
  warning:
    '<path d="M12 2.5l10.5 18h-21z" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path class="cut" d="M12 9.5v5"/><circle cx="12" cy="17.6" r="1.2" class="cutfill"/>',
  critical:
    '<path d="M8 2h8l6 6v8l-6 6H8l-6-6V8z" fill="currentColor"/><path class="cut" d="M12 7v6"/><circle cx="12" cy="16.6" r="1.2" class="cutfill"/>',
  info: '<circle cx="12" cy="12" r="10" fill="currentColor"/><path class="cut" d="M12 11v6"/><circle cx="12" cy="7.6" r="1.2" class="cutfill"/>',
  unknown:
    '<circle cx="12" cy="12" r="10" fill="currentColor"/><path class="cut" d="M9.6 9.6a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1.1.9-1.1 1.7"/><circle cx="12" cy="17" r="1.2" class="cutfill"/>',
};

export function StatusMark({ tone, size = 16 }: { tone: Tone; size?: number }) {
  return (
    <svg
      className="p1-mark"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      dangerouslySetInnerHTML={{ __html: MARKS[tone] }}
    />
  );
}

export interface ButtonProps {
  /** Verb-led label naming the object: "Reconnect P1Plug", not "Submit". */
  label: string;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive';
  loading?: boolean;
  disabled?: boolean;
  /** Required when disabled: the visible explanation. */
  reason?: string;
  type?: 'button' | 'submit';
  /** Addition. */
  onClick?: () => void;
  /** Addition: renders a router link with the button's appearance. */
  to?: string;
  /** Addition: renders an external link with the button's appearance. */
  href?: string;
}

export function Button({
  label,
  variant = 'primary',
  loading,
  disabled,
  reason,
  type = 'button',
  onClick,
  to,
  href,
}: ButtonProps) {
  const whyId = useId();
  const className = `p1-btn p1-btn--${variant}${loading ? ' is-loading' : ''}`;
  if (to)
    return (
      <Link className={className} to={to}>
        <span>{label}</span>
      </Link>
    );
  if (href)
    return (
      <a className={className} href={href}>
        <span>{label}</span>
      </a>
    );
  const why = disabled && reason ? reason : null;
  return (
    <span className="p1-btn-wrap">
      <button
        className={className}
        type={type}
        aria-busy={loading ? 'true' : undefined}
        aria-disabled={loading ? 'true' : undefined}
        disabled={!loading && disabled ? true : undefined}
        aria-describedby={why ? whyId : undefined}
        onClick={loading ? undefined : onClick}
      >
        {loading ? <span className="p1-spin" aria-hidden="true" /> : null}
        <span>{label}</span>
      </button>
      {why ? (
        <span className="p1-btn-why" id={whyId}>
          {why}
        </span>
      ) : null}
    </span>
  );
}

export interface FieldProps {
  label: string;
  id?: string;
  name?: string;
  type?: string;
  value?: string;
  hint?: string;
  /** Example value only, never the label. */
  example?: string;
  error?: string;
  optional?: boolean;
  optionalText?: string;
  autocomplete?: string;
  readonly?: boolean;
  /** Addition. */
  onChange?: (value: string) => void;
  /** Addition: scanner and keyboard-first screens focus their one field. */
  autoFocus?: boolean;
  /** Addition. */
  inputRef?: React.Ref<HTMLInputElement>;
}

export function Field({
  label,
  id,
  name,
  type = 'text',
  value,
  hint,
  example,
  error,
  optional,
  optionalText = '(optional)',
  autocomplete,
  readonly,
  onChange,
  autoFocus,
  inputRef,
}: FieldProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  const describedBy = [hint ? `${fieldId}-hint` : '', error ? `${fieldId}-err` : ''].filter(Boolean).join(' ');
  return (
    <div className={`p1-field${error ? ' has-error' : ''}`}>
      <label className="p1-label" htmlFor={fieldId}>
        {label}
        {optional ? (
          <>
            {' '}
            <span className="p1-optional">{optionalText}</span>
          </>
        ) : null}
      </label>
      {hint ? (
        <span className="p1-hint" id={`${fieldId}-hint`}>
          {hint}
        </span>
      ) : null}
      <input
        className="p1-input"
        id={fieldId}
        name={name ?? fieldId}
        type={type}
        value={value}
        placeholder={example}
        autoComplete={autocomplete}
        readOnly={readonly ? true : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy || undefined}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        autoFocus={autoFocus}
        ref={inputRef}
      />
      {error ? (
        <span className="p1-field-error" id={`${fieldId}-err`}>
          <StatusMark tone="critical" />
          <span>{error}</span>
        </span>
      ) : null}
    </div>
  );
}

export interface Column<Row> {
  key: string;
  label: string;
  numeric?: boolean;
  mono?: boolean;
  sorted?: 'asc' | 'desc';
  /** Addition: cell content; defaults to `row[key]` as text. */
  render?: (row: Row) => ReactNode;
}

export interface DataTableProps<Row> {
  /** The first column is the identifier. */
  columns: Column<Row>[];
  rows: Row[];
  caption?: string;
  filters?: { label: string; removeLabel?: string; onRemove?: () => void }[];
  filtersLabel?: string;
  /** Addition. */
  rowKey: (row: Row) => string;
}

export function DataTable<Row>({
  columns,
  rows,
  caption,
  filters,
  filtersLabel = 'Active filters',
  rowKey,
}: DataTableProps<Row>) {
  const cell = (row: Row, c: Column<Row>) =>
    c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '');
  return (
    <>
      {filters && filters.length > 0 ? (
        <div className="p1-filters" role="group" aria-label={filtersLabel}>
          {filters.map((f) => (
            <button
              key={f.label}
              type="button"
              className="p1-filter"
              aria-label={`${f.removeLabel ?? 'Remove filter'}: ${f.label}`}
              onClick={f.onRemove}
            >
              <span>{f.label}</span>
              <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="p1-table-wrap">
        <table className="p1-table">
          {caption ? <caption>{caption}</caption> : null}
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={c.sorted ? (c.sorted === 'desc' ? 'descending' : 'ascending') : undefined}
                  className={c.numeric ? 'is-num' : undefined}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((c, i) => {
                  const className = `${c.numeric ? 'is-num p1-num' : ''}${c.mono ? ' p1-data' : ''}`;
                  return i === 0 ? (
                    <th key={c.key} scope="row" className={className} data-label={c.label}>
                      {cell(row, c)}
                    </th>
                  ) : (
                    <td key={c.key} className={className} data-label={c.label}>
                      {cell(row, c)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export interface StatusIndicatorProps {
  tone: Tone;
  /** Human state, e.g. "P1Plug is verbonden". */
  label: string;
  /** Freshness or context, e.g. "Laatst gezien 18 s geleden". */
  detail?: string;
  /** Overline naming the state layer. */
  layer?: string;
}

export function StatusIndicator({ tone, label, detail, layer }: StatusIndicatorProps) {
  return (
    <span className="p1-status-block">
      {layer ? <span className="p1-status-layer">{layer}</span> : null}
      <span className={`p1-status p1-status--${tone}`}>
        <StatusMark tone={tone} />
        <span>{label}</span>
      </span>
      {detail ? <span className="p1-status-detail">{detail}</span> : null}
    </span>
  );
}

export interface ErrorStateProps {
  headline: string;
  impact: string;
  tone?: Tone;
  evidence?: string[];
  actionsLabel?: string;
  steps?: string[];
  /** Plain labels as in the design system, or (addition) labels with a handler or link. */
  actions?: (string | { label: string; onClick?: () => void; to?: string; href?: string })[];
  escalation?: string;
  reference?: string;
  referenceLabel?: string;
  technical?: [string, string][];
  technicalLabel?: string;
  /** Adds role="alert" for errors that appear after a user action. */
  live?: boolean;
}

export function ErrorState({
  headline,
  impact,
  tone = 'critical',
  evidence,
  actionsLabel,
  steps,
  actions,
  escalation,
  reference,
  referenceLabel = 'Reference',
  technical,
  technicalLabel = 'Technical details',
  live,
}: ErrorStateProps) {
  const id = useId();
  return (
    <section className={`p1-error p1-error--${tone}`} aria-labelledby={id} role={live ? 'alert' : undefined}>
      <h3 className="p1-error-title" id={id}>
        <StatusMark tone={tone} size={20} />
        <span>{headline}</span>
      </h3>
      <p className="p1-error-impact">{impact}</p>
      {evidence && evidence.length > 0 ? (
        <ul className="p1-evidence">
          {evidence.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}
      {actionsLabel ? <p className="p1-error-sub">{actionsLabel}</p> : null}
      {steps && steps.length > 0 ? (
        <ul className="p1-steps">
          {steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      ) : null}
      {actions && actions.length > 0 ? (
        <div className="p1-actions">
          {actions.map((a, i) => {
            const action = typeof a === 'string' ? { label: a } : a;
            return <Button key={action.label} {...action} variant={i === 0 ? 'primary' : 'secondary'} />;
          })}
        </div>
      ) : null}
      {escalation ? <p className="p1-escalation">{escalation}</p> : null}
      {reference ? (
        <p className="p1-ref">
          <span>{referenceLabel}</span> <code className="p1-data">{reference}</code>
        </p>
      ) : null}
      {technical ? (
        <details className="p1-tech">
          <summary>{technicalLabel}</summary>
          <dl className="p1-data">
            {technical.map(([term, value]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </section>
  );
}

export interface ProgressTrackProps {
  steps: string[];
  /** Zero-based index of the current step. */
  current: number;
  label?: string;
  progressText?: string;
}

export function ProgressTrack({ steps, current, label = 'Progress', progressText }: ProgressTrackProps) {
  return (
    <nav className="p1-progress" aria-label={label}>
      {progressText ? (
        <p className="p1-progress-text" role="status">
          {progressText}
        </p>
      ) : null}
      <ol className="p1-steps-track">
        {steps.map((step, i) => {
          const state = i < current ? 'done' : i === current ? 'current' : 'todo';
          return (
            <li key={step} className={`p1-step is-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
              <span className="p1-step-dot">
                {state === 'done' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M5 12.5l4.5 4.5L19 7.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span>{i + 1}</span>
                )}
              </span>
              <span className="p1-step-label">{step}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
